import React, { useEffect, useRef, useState } from 'react';
import NavigationBar from '@/components/ui/NavigationBar';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';

const Teleprompter = () => {
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/teleprompter', userTier);

  const [script, setScript] = useState('Hook: Stop wasting money on weak UGC — try this.\n\nCTA: Link in bio.');
  const [fontSize, setFontSize] = useState(28);
  const [speed, setSpeed] = useState(40); // px per second
  const [mirror, setMirror] = useState(false);
  const [running, setRunning] = useState(false);
  const [theme, setTheme] = useState('light');
  const [countdown, setCountdown] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let raf;
    if (running && containerRef.current) {
      const el = containerRef.current;
      const animate = () => {
        el.scrollTop += speed / 60;
        raf = requestAnimationFrame(animate);
      };
      raf = requestAnimationFrame(animate);
    }
    return () => raf && cancelAnimationFrame(raf);
  }, [running, speed]);

  // Track fullscreen changes
  useEffect(() => {
    const handler = () => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(!!fsElement);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!isFullscreen) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  const startWithCountdown = () => {
    if (running) {
      setRunning(false);
      return;
    }
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          setRunning(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const renderPreview = () => (
    <div className="mb-8 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
          <Icon name="Crown" size={14} />
          Creator / Elite
        </span>
        <span className="text-xs text-amber-700">Teleprompter</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Mobile-ready teleprompter for UGC shoots</h2>
      <p className="text-gray-700 mb-4">Adjust speed, size, and mirroring; run scripts from your workspace. Upgrade to Creator to access.</p>
      <Button onClick={() => (window.location.href = '/ugc#plans')} className="bg-amber-500 hover:bg-amber-600 text-white">
        View Creator Plans
      </Button>
    </div>
  );

  const ugcNavItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 text-foreground">
      <NavigationBar items={ugcNavItems} hideMore hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-5xl mx-auto px-4 py-10 mb-20">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-amber-500/20 via-slate-900 to-slate-950 px-6 py-8 mb-10 shadow-xl">
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-10 h-36 w-36 rounded-full bg-amber-500/25 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-amber-100 text-xs font-semibold border border-white/10 mb-3">
                <Icon name="Music" size={14} />
                Teleprompter
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Creator Teleprompter</h1>
              <p className="text-amber-100/80 max-w-2xl">Smooth scrolling script with speed, size, mirror, and fullscreen controls. Built for mobile shoots, tuned for UGC.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              {[{ label: 'Speed range', value: '10-100 px/s' }, { label: 'Mirror ready', value: 'Tablet + phone' }, { label: 'Countdown', value: '3s launch' }, { label: 'Night mode', value: 'Dark / Light' }].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white">
                  <p className="text-[11px] uppercase tracking-wide text-white/70">{item.label}</p>
                  <p className="text-lg font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!canAccess && renderPreview()}
        {canAccess && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 shadow-lg backdrop-blur space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white block mb-1">Script</label>
                  <textarea
                    className="w-full h-48 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50"
                    value={script}
                    onChange={(e) => setScript(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-white block">Font size</label>
                    <input type="range" min="16" max="48" value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} className="w-full accent-amber-400" />
                    <p className="text-xs text-white/60">{fontSize}px</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-white block">Speed</label>
                    <input type="range" min="10" max="100" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="w-full accent-amber-400" />
                    <p className="text-xs text-white/60">Scroll: {speed}px/sec</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-white">
                    <input type="checkbox" checked={mirror} onChange={(e) => setMirror(e.target.checked)} />
                    Mirror text
                  </label>
                  <Button size="sm" variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10" onClick={toggleFullscreen}>
                    <Icon name={isFullscreen ? 'Minimize' : 'Maximize'} size={14} className="mr-1" />
                    {isFullscreen ? 'Exit full screen' : 'Full screen'}
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-white">Theme</label>
                    <select
                      className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                      value={theme}
                      onChange={(e) => setTheme(e.target.value)}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <Button size="sm" variant="ghost" className="border-white/20 text-white bg-white/5 hover:bg-white/10" onClick={() => setScript('Hook: Stop wasting money on weak UGC — try this.\n\nCTA: Link in bio.')}>Quick reset</Button>
                </div>
                <Button fullWidth onClick={startWithCountdown} className="bg-amber-400 text-slate-950 hover:bg-amber-300 font-semibold">
                  {running ? 'Pause' : countdown > 0 ? `Starting in ${countdown}` : 'Start scroll'}
                </Button>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 shadow-lg backdrop-blur">
                <p className="text-xs uppercase text-white/70 mb-2">Templates</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Hook: POV — you need this before your next scroll.\n\nCTA: Link in bio.',
                    'Cold open, value, proof, CTA.\n\nCTA: Start your free trial today.',
                    'Three tips, then CTA.\n\nCTA: Save this for later.'
                  ].map((tpl) => (
                    <Button key={tpl} size="sm" variant="ghost" className="border-white/10 text-white bg-white/5 hover:bg-white/10" onClick={() => setScript(tpl)}>
                      Use template
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur">
                <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                <div className="flex items-center justify-between px-6 pt-4 text-white/70 text-xs uppercase tracking-[0.15em]">
                  <span>Auto-scroll</span>
                  <span>{mirror ? 'Mirrored' : 'Normal'} · {theme === 'dark' ? 'Dark' : 'Light'}</span>
                </div>
                <div
                  ref={containerRef}
                  className={`h-[520px] relative overflow-y-scroll px-6 pb-10 pt-2 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-black'}`}
                  style={{ transform: mirror ? 'scaleX(-1)' : 'none' }}
                >
                  {countdown > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-4xl font-bold rounded-3xl">
                      {countdown}
                    </div>
                  )}
                  <p style={{ fontSize }} className="whitespace-pre-wrap leading-relaxed">
                    {script}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <NovaChat />
    </div>
  );
};

const TeleprompterWithProvider = () => (
  <NovaChatProvider>
    <Teleprompter />
  </NovaChatProvider>
);

export default TeleprompterWithProvider;
